import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Expand } from 'lucide-react';
import { publicAPI } from '../lib/api';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';
import { Lightbox } from '../pages/GalleryPage';

// ─────────────────────────────────────────────────────────────────────────
// ModernGallery — the «Moderno» theme "Gallery".
// Two approved layouts (s140), chosen in Admin → Gallery → "Section layout"
// (`layout` on aurex_gallery_cfg): 'mosaic' (default) | 'stage'.
// Source (s140, Anthony): the Gallery Albums — each cover opens its album
// (/album/:id) and "view all" goes to /gallery_albums. With no albums it falls
// back to the Gallery manager photos and the /gallery lightbox. Header + CTA =
// CMS → Section Manager → Gallery — Section Configuration. Colours from theme
// tokens + graphite #16171a.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28';

const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

function Photo({ src, alt = '', className = '' }) {
  const [broken, setBroken] = React.useState(false);
  const url = img(src);
  if (!url || broken) return <span className={`block w-full h-full ${className}`} style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 35%, #fff), var(--color-accent, #ff9c1b))' }} />;
  return <img src={url} alt={alt} loading="lazy" onError={() => setBroken(true)} className={`w-full h-full object-cover ${className}`} />;
}

function Header({ cmsConfig, albums }) {
  const tt = useT();
  const title = tt(cmsConfig.title) || 'Gallery';
  const words = title.trim().split(/\s+/);
  const last = words.length > 1 ? words.pop() : null;
  const set = tt(cmsConfig.cta_url);
  const ctaUrl = albums && (!set || set === '/gallery') ? '/gallery_albums' : (set || '/gallery');
  const ctaText = tt(cmsConfig.cta_text) || 'View all photos';
  const target = cmsConfig.cta_new_tab ? '_blank' : '_self';
  const ctaProps = { className: 'mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end', style: { color: 'var(--accent-strong, #b86500)' }, 'data-testid': 'gallery-view-all' };
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-14">
      <div className="flex flex-col gap-4 max-w-2xl">
        {tt(cmsConfig.eyebrow) && (
          <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
            <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow)}
          </div>
        )}
        <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="gallery-title">
          {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
        </h2>
        {tt(cmsConfig.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(cmsConfig.subtitle)}</p>}
      </div>
      {/^https?:\/\//i.test(ctaUrl)
        ? <a href={ctaUrl} target={target} rel="noopener noreferrer" {...ctaProps}>{ctaText} <ArrowRight className="w-4 h-4" /></a>
        : <Link to={ctaUrl} target={target} {...ctaProps}>{ctaText} <ArrowRight className="w-4 h-4" /></Link>}
    </div>
  );
}

const Eyebrow = ({ children, light }) => (
  <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: light ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}>{children}</span>
);

/* ── Editorial Mosaic (default) ───────────────────────────────────────────────
   One large tile (2×2) + up to five more on a 3-column grid; the caption slides
   up on hover/focus (always shown for albums, since the title is the link). */
function MosaicVariant({ list, open, albums }) {
  const tt = useT();
  const pics = list.slice(0, 6);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
      {pics.map((g, i) => (
        <button key={g.id} type="button" onClick={() => open(i)} data-testid={`gallery-item-${g.id}`}
          className={`group relative overflow-hidden rounded-[3px] text-left ${i === 0 ? 'col-span-2 lg:row-span-2 aspect-[4/3] lg:aspect-auto' : 'aspect-[4/3]'}`}>
          <Photo src={g.image} alt={tt(g.title)} className="transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
          <span className={`absolute inset-x-0 bottom-0 p-4 lg:p-5 flex flex-col gap-1 transition-[opacity,transform] duration-200 ease-out ${albums ? '' : 'translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100'}`}
            style={{ background: 'linear-gradient(to top, rgba(10,10,12,.78), rgba(10,10,12,0))' }}>
            {tt(g.category) && <Eyebrow light>{tt(g.category)}</Eyebrow>}
            <span className="text-white font-semibold" style={{ fontFamily: SERIF, fontSize: i === 0 ? '1.5rem' : '1.05rem', lineHeight: 1.2 }}>{tt(g.title)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/* ── Stage + Index ──────────────────────────────────────────────────
   A large "stage" photo with its caption; beside it a numbered index of
   the other moments (hover/click switches the stage, click stage = zoom). */
function StageVariant({ list, open, albums }) {
  const tt = useT();
  const pics = list.slice(0, 6);
  const [at, setAt] = React.useState(0);
  const cur = pics[at];
  return (
    <div className="grid gap-8 lg:gap-14 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] items-start">
      <button type="button" onClick={() => open(at)} className="group relative block aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-[3px] text-left" style={{ background: GRAPHITE }} aria-label={`${albums ? 'Open' : 'Enlarge'} ${tt(cur.title)}`} data-testid="gallery-stage">
        {pics.map((g, i) => (
          <span key={g.id} className="absolute inset-0 transition-opacity duration-300 ease-out" style={{ opacity: i === at ? 1 : 0 }} aria-hidden={i !== at}><Photo src={g.image} alt="" /></span>
        ))}
        <span className="absolute inset-x-0 bottom-0 p-5 lg:p-7 flex items-end justify-between gap-4" style={{ background: 'linear-gradient(to top, rgba(10,10,12,.72), rgba(10,10,12,0))' }}>
          <span className="flex flex-col gap-1.5">
            {tt(cur.category) && <Eyebrow light>{tt(cur.category)}</Eyebrow>}
            <span className="text-white font-semibold" style={{ fontFamily: SERIF, fontSize: 'clamp(1.3rem, 2vw, 1.8rem)', lineHeight: 1.2 }}>{tt(cur.title)}</span>
            {tt(cur.summary) && <span className="hidden sm:block text-white/75 text-[15px]">{tt(cur.summary)}</span>}
          </span>
          <span className="w-10 h-10 flex-none rounded-full flex items-center justify-center text-white bg-white/15 transition-colors duration-150 group-hover:bg-white/25">{albums ? <ArrowRight className="w-4 h-4" /> : <Expand className="w-4 h-4" />}</span>
        </span>
      </button>
      <ol className="m-0 p-0 list-none border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
        {pics.map((g, i) => (
          <li key={g.id} className="border-b" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
            <button type="button" onMouseEnter={() => setAt(i)} onFocus={() => setAt(i)} onClick={() => (i === at ? open(i) : setAt(i))}
              className="w-full grid grid-cols-[auto_56px_minmax(0,1fr)] gap-4 items-center py-3.5 text-left" data-testid={`gallery-item-${g.id}`}>
              <span className="text-sm font-bold tabular-nums transition-colors duration-200" style={{ color: i === at ? 'var(--accent-strong, #b86500)' : 'rgba(20,26,58,.4)' }}>{String(i + 1).padStart(2, '0')}</span>
              <span className="block w-14 h-10 overflow-hidden rounded-[2px] transition-opacity duration-200" style={{ opacity: i === at ? 1 : 0.6 }}><Photo src={g.image} alt="" /></span>
              <span className="flex flex-col min-w-0">
                <span className="font-semibold truncate transition-colors duration-200" style={{ fontFamily: SERIF, fontSize: '1.05rem', color: i === at ? 'var(--accent-strong, #b86500)' : 'var(--color-heading, #1a2332)' }}>{tt(g.title)}</span>
                {tt(g.category) && <span className="text-xs" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(g.category)}</span>}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

export default function ModernGallery({ items, cmsConfig = {} }) {
  const tt = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [albums, setAlbums] = React.useState(null);
  const [lb, setLb] = React.useState(-1);
  React.useEffect(() => { publicAPI.getGalleryAlbums().then(r => setAlbums(Array.isArray(r.data) ? r.data : [])).catch(() => setAlbums([])); }, []);
  const albumList = (albums || []).filter(a => a.cover_image && tt(a.title))
    .map(a => ({ id: a.id, title: a.title, summary: plain(tt(a.description)), image: a.cover_image, category: 'Album' }));
  const useAlbums = albumList.length > 0;
  const photos = (items || []).filter(g => g.image && itemHasLocale(g.title, lang));
  if (albums === null) return null;
  const list = useAlbums ? albumList : photos;
  if (!list.length) return null;
  // The lightbox renders plain strings — resolve the locale first.
  const lbItems = photos.map(g => ({ ...g, title: tt(g.title), summary: tt(g.summary) }));
  const open = useAlbums ? (i) => navigate(`/album/${list[i].id}`) : setLb;
  const V = cmsConfig.layout === 'stage' ? StageVariant : MosaicVariant;
  return (
    <section id="gallery" data-testid="gallery-section" className="bg-white">
      <div className={shell}>
        <Header cmsConfig={cmsConfig} albums={useAlbums} />
        <V list={list} open={open} albums={useAlbums} />
      </div>
      {!useAlbums && lb >= 0 && (
        <Lightbox items={lbItems} currentIndex={lb} onClose={() => setLb(-1)}
          onNext={() => setLb(i => (i + 1) % lbItems.length)} onPrev={() => setLb(i => (i - 1 + lbItems.length) % lbItems.length)} />
      )}
    </section>
  );
}
